import { useEffect, useRef, useState, type Ref, type ReactNode } from "react";
import expandIcon from "@/assets/figma/component-bar/expand.svg";
import toolsIcon from "@/assets/figma/component-bar/tools.svg";
import spacingIcon from "@/assets/figma/component-bar/spacing.svg";
import addIcon from "@/assets/figma/component-bar/add.svg";
import toolbarSeparator from "@/assets/figma/component-bar/toolbar-separator.svg";

/** Shared glass feed and conversation rail, supplied with live content. */
export interface RightRailSlots {
  feed?: ReactNode;
  feedUnavailable?: boolean;
  single?: ReactNode;
  mode?: "feed" | "single";
  rootRef?: Ref<HTMLDivElement>;
  accessory?: ReactNode;
  onAddComponents?: () => void;
  addComponentsRef?: Ref<HTMLButtonElement>;
  addComponentsOpen?: boolean;
}

export function RightRail({ feed, single, mode = "feed", rootRef, accessory, onAddComponents, addComponentsRef, addComponentsOpen, feedUnavailable = false }: RightRailSlots) {
  const [feedMode, setFeedMode] = useState<"stacked" | "expanded">("expanded");
  const [feedCollapsed, setFeedCollapsed] = useState(() => window.matchMedia("(max-width: 1720px)").matches);
  const stripRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const content = mode === "feed" ? feed : single;
  const label = mode === "feed" ? "信息流" : "商机";

  useEffect(() => {
    if (mode !== "feed") return;
    const reveal = () => { setFeedCollapsed(false); setFeedMode("expanded"); };
    document.addEventListener("component-transfer-show-rail", reveal);
    return () => document.removeEventListener("component-transfer-show-rail", reveal);
  }, [mode]);

  // Collapse on a new terminal failure; reopening keeps the retry action reachable.
  const unavailableSeen = useRef(false);
  useEffect(() => {
    const unavailable = mode === "feed" && feedUnavailable;
    if (unavailable && !unavailableSeen.current) {
      const restoreFocus = contentRef.current?.contains(document.activeElement);
      setFeedCollapsed(true);
      if (restoreFocus) requestAnimationFrame(() => stripRef.current?.focus());
    }
    unavailableSeen.current = unavailable;
  }, [mode, feedUnavailable]);

  const collapseFeed = () => { setFeedCollapsed(true); requestAnimationFrame(() => stripRef.current?.focus()); };

  useEffect(() => {
    const compact = window.matchMedia("(max-width: 1720px)");
    const syncViewport = () => setFeedCollapsed(compact.matches);
    compact.addEventListener("change", syncViewport);
    return () => compact.removeEventListener("change", syncViewport);
  }, []);

  useEffect(() => {
    if (mode !== "feed" || feedCollapsed) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !document.querySelector('[role="dialog"][aria-modal="true"]') && window.matchMedia("(max-width: 1720px)").matches) {
        event.preventDefault();
        setFeedCollapsed(true);
        requestAnimationFrame(() => stripRef.current?.focus());
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mode, feedCollapsed]);

  return <div ref={rootRef} className="merchant-right-rail merchant-home-rail" data-testid="merchant-home-rail" data-shell="rail" data-mode={mode} data-feed-mode={mode === "feed" ? feedMode : undefined} data-feed-collapsed={mode === "feed" && feedCollapsed || undefined}>
    {mode === "feed" && <div className="merchant-right-rail__feed-controls">
      <div className="merry-component-bar__toolbar-surface merchant-right-rail__tools-surface">
        <span aria-hidden="true" className="merry-component-bar__toolbar-glass" />
        <span aria-hidden="true" className="merry-component-bar__toolbar-tools"><img alt="" src={toolsIcon} /></span>
        <img alt="" aria-hidden="true" className="merry-component-bar__toolbar-separator" src={toolbarSeparator} />
        <button type="button" className="merry-component-bar__toolbar-button" aria-label={feedMode === "stacked" ? "向下展开组件栏" : "向上折叠组件栏"} aria-expanded={feedMode === "expanded"} onClick={() => setFeedMode(current => current === "stacked" ? "expanded" : "stacked")}><img alt="" src={spacingIcon} /></button>
        <span className="merchant-right-rail__add-control"><button ref={addComponentsRef} type="button" className="merry-component-bar__toolbar-button" aria-label="添加小组件" aria-haspopup="dialog" aria-expanded={Boolean(addComponentsOpen)} onClick={onAddComponents}><img alt="" src={addIcon} /></button><span role="tooltip" className="merchant-right-rail__add-tooltip">添加组件</span></span>
        <span aria-hidden="true" className="merry-component-bar__toolbar-stroke" />
      </div>
      <div className="merry-component-bar__toolbar-surface merchant-right-rail__collapse-surface"><span aria-hidden="true" className="merry-component-bar__toolbar-glass" /><button type="button" className="merry-component-bar__toolbar-toggle" aria-label="向右收起侧边栏" onClick={collapseFeed}><img alt="" src={expandIcon} /></button><span aria-hidden="true" className="merry-component-bar__toolbar-stroke" /></div>
    </div>}
    <div ref={contentRef} tabIndex={-1} className={`merchant-right-rail__content${mode === "feed" ? " merchant-home-rail__desktop" : ""}`} aria-label={label} inert={mode === "feed" && feedCollapsed}>
      <div className="merchant-right-rail__content-body">{content}</div>
    </div>
    {mode === "feed" && <button ref={stripRef} type="button" className="merchant-right-rail__strip" aria-label="打开组件栏" aria-expanded={!feedCollapsed} onClick={() => { setFeedCollapsed(false); requestAnimationFrame(() => contentRef.current?.focus()); }}><img alt="" src={toolsIcon} /></button>}
    {accessory}
  </div>;
}
